/**
 * Data Access Layer for Collective Selection
 *
 * A selection converges a fixed roster of account users on a subset of
 * happenings across ordered rounds (nominate → allocate). The defining property
 * is temporal blindness: ballots are invisible until a round is revealed, an
 * invariant enforced primarily in RLS (see migration 080) and mirrored by the
 * state machine here.
 *
 * Conventions follow programs.ts: getClient(), account scoping via
 * getCurrentAccountId(), TABLES.*. Child rows (rounds, ballots, entries,
 * recusals, voters, picks) carry no account_id — RLS scopes them via their
 * parent, and queries here narrow by the parent key.
 *
 * The acting user id (a voter) is always passed in explicitly; the API sources
 * it from the request JWT's `sub`. This module only holds the account id in
 * context.
 */

import { getClient } from './client.js';
import { TABLES } from './tables.js';
import { getCurrentAccountId } from './account-context.js';
import type {
  Selection,
  SelectionCreate,
  SelectionUpdate,
  SelectionVoter,
  SelectionRecusal,
  SelectionRecusalCreate,
  SelectionRound,
  SelectionRoundCreate,
  SelectionRoundUpdate,
  RoundState,
  SelectionBallot,
  SelectionBallotEntry,
  SelectionBallotWithEntries,
  BallotEntryInput,
  SelectionIndividualPick,
  SelectionIndividualPickCreate,
  SelectionAuditEvent,
  SelectionAuditEventCreate,
  NominateRoundConfig,
  AllocateRoundConfig,
  CutoffRule,
  TallyRow,
} from './types.js';

/**
 * Thrown when a ballot or round fails a business-rule check. The API maps this
 * to 400 (bad request); anything else bubbles up as 500.
 */
export class SelectionValidationError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'SelectionValidationError';
  }
}

// Legal round state transitions. Forward-only; 'revealed' is reachable only
// from 'closed' and is terminal (irreversible). 'closed' is reachable from
// 'open' (owner action or closes_at passing, handled by the caller).
const ROUND_TRANSITIONS: Record<RoundState, RoundState[]> = {
  draft: ['open'],
  open: ['closed'],
  closed: ['revealed'],
  revealed: [],
};

// ============================================
// Suggested defaults (advisory — surfaced in the UI as suggestions, never
// applied as hard truth). See "Suggested max_picks" in the spec.
// ============================================

/**
 * ceil(target_count * 1.6 / voter_count), where target_count =
 * round(scope_size * target_pct). The 1.6 is an assumed overlap factor — a
 * guess. Callers must present the result as a suggestion, not a derived truth.
 */
export function computeSuggestedMaxPicks(
  scopeSize: number,
  targetPct: number,
  voterCount: number,
): number {
  if (voterCount <= 0) return 0;
  const targetCount = Math.round(scopeSize * targetPct);
  return Math.ceil((targetCount * 1.6) / voterCount);
}

// ============================================
// Selections
// ============================================

export async function getSelection(id: string): Promise<Selection | null> {
  const { data, error } = await getClient()
    .from(TABLES.selections)
    .select('*')
    .eq('id', id)
    .single();

  if (error) {
    if (error.code === 'PGRST116') return null;
    throw error;
  }
  return data as Selection;
}

export async function listSelections(filters?: {
  state?: Selection['state'];
  query?: string;
}): Promise<Selection[]> {
  let query = getClient().from(TABLES.selections).select('*');

  const accountId = getCurrentAccountId();
  if (accountId) query = query.eq('account_id', accountId);
  if (filters?.state) query = query.eq('state', filters.state);
  if (filters?.query && filters.query.trim()) {
    query = query.ilike('title', `%${filters.query.trim()}%`);
  }

  const { data, error } = await query.order('created_at', { ascending: false });
  if (error) throw error;
  return (data || []) as Selection[];
}

export async function createSelection(input: SelectionCreate): Promise<Selection> {
  const accountId = getCurrentAccountId();
  if (!accountId) throw new Error('createSelection: no account context set');

  const { data, error } = await getClient()
    .from(TABLES.selections)
    .insert({
      account_id: accountId,
      owner_id: input.owner_id,
      title: input.title,
      description: input.description ?? null,
      scope: input.scope ?? {},
      target_pct: input.target_pct ?? null,
      state: input.state ?? 'draft',
      settings: input.settings ?? {},
    })
    .select()
    .single();

  if (error) throw error;
  return data as Selection;
}

export async function updateSelection(id: string, input: SelectionUpdate): Promise<Selection> {
  const { data, error } = await getClient()
    .from(TABLES.selections)
    .update(input)
    .eq('id', id)
    .select()
    .single();

  if (error) throw error;
  return data as Selection;
}

export async function deleteSelection(id: string): Promise<void> {
  const { error } = await getClient().from(TABLES.selections).delete().eq('id', id);
  if (error) throw error;
}

// ============================================
// Voter roster (fixed before round 1 opens)
// ============================================

export async function listSelectionVoters(selectionId: string): Promise<SelectionVoter[]> {
  const { data, error } = await getClient()
    .from(TABLES.selection_voters)
    .select('*')
    .eq('selection_id', selectionId);

  if (error) throw error;
  return (data || []) as SelectionVoter[];
}

/**
 * Replace the roster in one go (delete current rows, insert new). The roster is
 * meant to be fixed before round 1 opens; enforcing that gate is the caller's job.
 */
export async function setSelectionVoters(selectionId: string, userIds: string[]): Promise<void> {
  const client = getClient();

  const { error: delError } = await client
    .from(TABLES.selection_voters)
    .delete()
    .eq('selection_id', selectionId);
  if (delError) throw delError;

  const unique = Array.from(new Set(userIds));
  if (unique.length > 0) {
    const rows = unique.map((user_id) => ({ selection_id: selectionId, user_id }));
    const { error: insError } = await client.from(TABLES.selection_voters).insert(rows);
    if (insError) throw insError;
  }
}

// ============================================
// Recusals
// ============================================

export async function listRecusals(selectionId: string): Promise<SelectionRecusal[]> {
  const { data, error } = await getClient()
    .from(TABLES.selection_recusals)
    .select('*')
    .eq('selection_id', selectionId);

  if (error) throw error;
  return (data || []) as SelectionRecusal[];
}

export async function addRecusal(input: SelectionRecusalCreate): Promise<SelectionRecusal> {
  const { data, error } = await getClient()
    .from(TABLES.selection_recusals)
    .insert({
      selection_id: input.selection_id,
      user_id: input.user_id,
      happening_id: input.happening_id,
      reason: input.reason ?? null,
    })
    .select()
    .single();

  if (error) throw error;
  return data as SelectionRecusal;
}

export async function removeRecusal(id: string): Promise<void> {
  const { error } = await getClient().from(TABLES.selection_recusals).delete().eq('id', id);
  if (error) throw error;
}

/** Happening ids a specific voter is recused from, for this selection. */
export async function recusedHappeningIds(selectionId: string, voterId: string): Promise<Set<string>> {
  const { data, error } = await getClient()
    .from(TABLES.selection_recusals)
    .select('happening_id')
    .eq('selection_id', selectionId)
    .eq('user_id', voterId);

  if (error) throw error;
  return new Set((data || []).map((r: { happening_id: string }) => r.happening_id));
}

// ============================================
// Rounds + state machine
// ============================================

export async function listRounds(selectionId: string): Promise<SelectionRound[]> {
  const { data, error } = await getClient()
    .from(TABLES.selection_rounds)
    .select('*')
    .eq('selection_id', selectionId)
    .order('ordinal');

  if (error) throw error;
  return (data || []) as SelectionRound[];
}

export async function getRound(id: string): Promise<SelectionRound | null> {
  const { data, error } = await getClient()
    .from(TABLES.selection_rounds)
    .select('*')
    .eq('id', id)
    .single();

  if (error) {
    if (error.code === 'PGRST116') return null;
    throw error;
  }
  return data as SelectionRound;
}

/**
 * Create the next round. Ordinal defaults to max(existing) + 1. Config falls
 * back to the kind's defaults (spec-documented); the owner edits it before the
 * round opens. max_picks is intentionally left unset for nominate — the API
 * offers computeSuggestedMaxPicks() as a suggestion the owner confirms.
 */
export async function createRound(input: SelectionRoundCreate): Promise<SelectionRound> {
  const existing = await listRounds(input.selection_id);
  const ordinal = input.ordinal ?? (existing.reduce((m, r) => Math.max(m, r.ordinal), 0) + 1);

  const defaultConfig =
    input.kind === 'nominate'
      ? ({ wildcards: 2, require_note: false } satisfies Partial<NominateRoundConfig>)
      : ({ budget: 100, per_item_cap: 30 } satisfies Partial<AllocateRoundConfig>);

  const { data, error } = await getClient()
    .from(TABLES.selection_rounds)
    .insert({
      selection_id: input.selection_id,
      ordinal,
      kind: input.kind,
      state: input.state ?? 'draft',
      opens_at: input.opens_at ?? null,
      closes_at: input.closes_at ?? null,
      config: { ...defaultConfig, ...(input.config ?? {}) },
    })
    .select()
    .single();

  if (error) throw error;
  return data as SelectionRound;
}

/** Update a round's schedule/config while it is still being set up. */
export async function updateRound(id: string, patch: SelectionRoundUpdate): Promise<SelectionRound> {
  const { data, error } = await getClient()
    .from(TABLES.selection_rounds)
    .update(patch)
    .eq('id', id)
    .select()
    .single();

  if (error) throw error;
  return data as SelectionRound;
}

/**
 * Transition a round's state, enforcing the forward-only machine. Reveal is
 * reachable only from closed and is irreversible. Throws SelectionValidationError
 * on an illegal transition. (Who is allowed to transition — the owner — is
 * enforced at the API layer and by RLS.)
 */
export async function transitionRound(id: string, to: RoundState): Promise<SelectionRound> {
  const round = await getRound(id);
  if (!round) throw new SelectionValidationError('Round not found');

  const allowed = ROUND_TRANSITIONS[round.state] ?? [];
  if (!allowed.includes(to)) {
    throw new SelectionValidationError(
      `Illegal round transition ${round.state} → ${to} (allowed: ${allowed.join(', ') || 'none'})`,
    );
  }
  return updateRound(id, { state: to });
}

// ============================================
// Ballots
// ============================================

async function getEntries(ballotId: string): Promise<SelectionBallotEntry[]> {
  const { data, error } = await getClient()
    .from(TABLES.selection_ballot_entries)
    .select('*')
    .eq('ballot_id', ballotId);

  if (error) throw error;
  return (data || []) as SelectionBallotEntry[];
}

/** The voter's own ballot for a round (with entries), or null if none yet. */
export async function getVoterBallot(
  roundId: string,
  voterId: string,
): Promise<SelectionBallotWithEntries | null> {
  const { data, error } = await getClient()
    .from(TABLES.selection_ballots)
    .select('*')
    .eq('round_id', roundId)
    .eq('voter_id', voterId)
    .single();

  if (error) {
    if (error.code === 'PGRST116') return null;
    throw error;
  }
  const ballot = data as SelectionBallot;
  const entries = await getEntries(ballot.id);
  return { ...ballot, entries };
}

/** Create the voter's draft ballot if it doesn't exist yet. */
export async function ensureBallot(roundId: string, voterId: string): Promise<SelectionBallot> {
  const existing = await getClient()
    .from(TABLES.selection_ballots)
    .select('*')
    .eq('round_id', roundId)
    .eq('voter_id', voterId)
    .single();

  if (!existing.error) return existing.data as SelectionBallot;
  if (existing.error.code !== 'PGRST116') throw existing.error;

  const { data, error } = await getClient()
    .from(TABLES.selection_ballots)
    .insert({ round_id: roundId, voter_id: voterId, status: 'draft' })
    .select()
    .single();

  if (error) throw error;
  return data as SelectionBallot;
}

/**
 * Save a draft ballot: replaces the ballot's entries wholesale and keeps status
 * 'draft'. Autosave-friendly. Does NOT run submit validation — the client shows
 * a live total, but nothing is enforced until submit. Only allowed while the
 * round is open.
 */
export async function saveBallotEntries(
  roundId: string,
  voterId: string,
  entries: BallotEntryInput[],
): Promise<SelectionBallotWithEntries> {
  const round = await getRound(roundId);
  if (!round) throw new SelectionValidationError('Round not found');
  if (round.state !== 'open') {
    throw new SelectionValidationError(`Round is ${round.state}; ballots are only editable while open`);
  }

  const ballot = await ensureBallot(roundId, voterId);
  const client = getClient();

  const { error: delError } = await client
    .from(TABLES.selection_ballot_entries)
    .delete()
    .eq('ballot_id', ballot.id);
  if (delError) throw delError;

  if (entries.length > 0) {
    const rows = entries.map((e) => ({
      ballot_id: ballot.id,
      happening_id: e.happening_id,
      points: e.points ?? null,
      is_wildcard: e.is_wildcard ?? false,
      note: e.note ?? null,
    }));
    const { error: insError } = await client.from(TABLES.selection_ballot_entries).insert(rows);
    if (insError) throw insError;
  }

  // Editing a previously-submitted ballot drops it back to draft (the caller
  // re-submits); edits while open are permitted but logged by the API.
  if (ballot.status === 'submitted') {
    await client
      .from(TABLES.selection_ballots)
      .update({ status: 'draft', submitted_at: null })
      .eq('id', ballot.id);
  }

  const saved = await getVoterBallot(roundId, voterId);
  return saved!;
}

/**
 * Validate a set of entries against a round's rules + the voter's recusals.
 * Pure and side-effect-free so it can back both a submit and a client-side
 * dry-run. Throws SelectionValidationError on the first failure.
 *
 * For allocate rounds, shortlist membership is checked only when the round's
 * config carries `shortlist_happening_ids` (populated when the shortlist is
 * generated at the prior round's reveal — build-order step 5).
 */
export function validateBallotEntries(
  round: SelectionRound,
  entries: BallotEntryInput[],
  recused: Set<string>,
): void {
  for (const e of entries) {
    if (recused.has(e.happening_id)) {
      throw new SelectionValidationError(`Entry on a recused happening (${e.happening_id})`);
    }
  }

  const seen = new Set<string>();
  for (const e of entries) {
    if (seen.has(e.happening_id)) {
      throw new SelectionValidationError(`Duplicate entry for happening ${e.happening_id}`);
    }
    seen.add(e.happening_id);
  }

  if (round.kind === 'nominate') {
    const cfg = round.config as NominateRoundConfig;
    if (typeof cfg.max_picks !== 'number') {
      throw new SelectionValidationError('Round is not configured (max_picks unset)');
    }
    const regular = entries.filter((e) => !e.is_wildcard);
    const wildcards = entries.filter((e) => e.is_wildcard);
    if (regular.length > cfg.max_picks) {
      throw new SelectionValidationError(`Too many picks: ${regular.length} > max_picks ${cfg.max_picks}`);
    }
    if (typeof cfg.wildcards === 'number' && wildcards.length > cfg.wildcards) {
      throw new SelectionValidationError(`Too many wildcards: ${wildcards.length} > ${cfg.wildcards}`);
    }
    if (cfg.require_note) {
      for (const e of entries) {
        if (!e.note || !e.note.trim()) {
          throw new SelectionValidationError(`A note is required on every nomination (${e.happening_id})`);
        }
      }
    }
    return;
  }

  // allocate
  const cfg = round.config as AllocateRoundConfig & { shortlist_happening_ids?: string[] };
  const shortlist = Array.isArray(cfg.shortlist_happening_ids)
    ? new Set(cfg.shortlist_happening_ids)
    : null;

  let sum = 0;
  for (const e of entries) {
    const pts = e.points ?? 0;
    if (!Number.isInteger(pts) || pts < 0) {
      throw new SelectionValidationError(`Points must be a non-negative integer (${e.happening_id})`);
    }
    if (typeof cfg.per_item_cap === 'number' && pts > cfg.per_item_cap) {
      throw new SelectionValidationError(`Points ${pts} exceed per_item_cap ${cfg.per_item_cap} (${e.happening_id})`);
    }
    if (shortlist && !shortlist.has(e.happening_id)) {
      throw new SelectionValidationError(`Happening ${e.happening_id} is not on the shortlist`);
    }
    if (typeof cfg.require_note_above === 'number' && pts > cfg.require_note_above) {
      if (!e.note || !e.note.trim()) {
        throw new SelectionValidationError(`A note is required for allocations above ${cfg.require_note_above} (${e.happening_id})`);
      }
    }
    sum += pts;
  }

  if (typeof cfg.budget === 'number' && sum !== cfg.budget) {
    throw new SelectionValidationError(`Points must sum to exactly ${cfg.budget} (got ${sum})`);
  }
}

/**
 * The shortlist an allocate round votes over. Prefer the round's own snapshot
 * (taken at creation), but fall back to the latest revealed nominate round's
 * shortlist when the snapshot is missing/empty — e.g. the allocate round was
 * created before the nominate round was revealed. Returns [] for non-allocate
 * rounds or when no shortlist exists anywhere yet.
 */
export async function getEffectiveShortlist(round: SelectionRound): Promise<string[]> {
  if (round.kind !== 'allocate') return [];
  const own = (round.config as { shortlist_happening_ids?: string[] }).shortlist_happening_ids;
  if (Array.isArray(own) && own.length > 0) return own;

  const rounds = await listRounds(round.selection_id);
  const revealed = rounds
    .filter((r) => r.kind === 'nominate' && r.state === 'revealed')
    .sort((a, b) => b.ordinal - a.ordinal)[0];
  const fallback = (revealed?.config as { shortlist_happening_ids?: string[] })?.shortlist_happening_ids;
  return Array.isArray(fallback) ? fallback : [];
}

/**
 * Persist the current entries and mark the ballot submitted, after server-side
 * validation. Re-reads the round + recusals so the client cannot spoof them.
 * Only 'submitted' ballots count toward quorum and tally.
 */
export async function submitBallot(
  selectionId: string,
  roundId: string,
  voterId: string,
  entries: BallotEntryInput[],
): Promise<SelectionBallotWithEntries> {
  const round = await getRound(roundId);
  if (!round) throw new SelectionValidationError('Round not found');
  if (round.state !== 'open') {
    throw new SelectionValidationError(`Round is ${round.state}; cannot submit`);
  }

  const recused = await recusedHappeningIds(selectionId, voterId);
  // For allocate rounds, validate against the effective shortlist (snapshot or
  // fallback), not just the possibly-empty snapshot on this round.
  let roundForValidation = round;
  if (round.kind === 'allocate') {
    const effective = await getEffectiveShortlist(round);
    roundForValidation = {
      ...round,
      config: { ...round.config, shortlist_happening_ids: effective } as typeof round.config,
    };
  }
  validateBallotEntries(roundForValidation, entries, recused);

  // Save entries then flip status to submitted.
  await saveBallotEntries(roundId, voterId, entries);
  const ballot = await ensureBallot(roundId, voterId);

  const { error } = await getClient()
    .from(TABLES.selection_ballots)
    .update({ status: 'submitted', submitted_at: new Date().toISOString() })
    .eq('id', ballot.id);
  if (error) throw error;

  const submitted = await getVoterBallot(roundId, voterId);
  return submitted!;
}

/**
 * All ballots (with entries) for a round — the tally source. RLS gates this:
 * pre-reveal it returns only the caller's own ballot; once revealed, all of
 * them. Call under service_role only for a deliberately-audited pre-reveal read.
 */
export async function listRoundBallots(roundId: string): Promise<SelectionBallotWithEntries[]> {
  const { data, error } = await getClient()
    .from(TABLES.selection_ballots)
    .select('*')
    .eq('round_id', roundId);

  if (error) throw error;
  const ballots = (data || []) as SelectionBallot[];

  const withEntries = await Promise.all(
    ballots.map(async (b) => ({ ...b, entries: await getEntries(b.id) })),
  );
  return withEntries;
}

/**
 * Aggregate, identity-safe progress the owner may see while a round is open:
 * how many of the expected ballots are submitted, and which voters have
 * submitted (chasing, not peeking — no ballot contents). Never exposes entries.
 */
export async function getRoundProgress(
  selectionId: string,
  roundId: string,
): Promise<{
  expected: number;
  submitted: number;
  submittedVoterIds: string[];
  draftVoterIds: string[];
}> {
  const [voters, ballotsRes] = await Promise.all([
    listSelectionVoters(selectionId),
    getClient().from(TABLES.selection_ballots).select('voter_id,status').eq('round_id', roundId),
  ]);

  if (ballotsRes.error) throw ballotsRes.error;
  const rows = (ballotsRes.data || []) as { voter_id: string; status: string }[];
  const submittedVoterIds = rows.filter((r) => r.status === 'submitted').map((r) => r.voter_id);
  // Voters who started a ballot but haven't submitted (status still 'draft').
  const draftVoterIds = rows.filter((r) => r.status === 'draft').map((r) => r.voter_id);
  return {
    expected: voters.length,
    submitted: submittedVoterIds.length,
    submittedVoterIds,
    draftVoterIds,
  };
}

// ============================================
// Individual picks (published, one per voter)
// ============================================

export async function listIndividualPicks(selectionId: string): Promise<SelectionIndividualPick[]> {
  const { data, error } = await getClient()
    .from(TABLES.selection_individual_picks)
    .select('*')
    .eq('selection_id', selectionId);

  if (error) throw error;
  return (data || []) as SelectionIndividualPick[];
}

/** Upsert the one pick a voter is published under (unique per selection+voter). */
export async function setIndividualPick(
  input: SelectionIndividualPickCreate,
): Promise<SelectionIndividualPick> {
  const { data, error } = await getClient()
    .from(TABLES.selection_individual_picks)
    .upsert(
      {
        selection_id: input.selection_id,
        voter_id: input.voter_id,
        happening_id: input.happening_id,
        justification: input.justification ?? null,
      },
      { onConflict: 'selection_id,voter_id' },
    )
    .select()
    .single();

  if (error) throw error;
  return data as SelectionIndividualPick;
}

export async function removeIndividualPick(selectionId: string, voterId: string): Promise<void> {
  const { error } = await getClient()
    .from(TABLES.selection_individual_picks)
    .delete()
    .eq('selection_id', selectionId)
    .eq('voter_id', voterId);
  if (error) throw error;
}

// ============================================
// Audit log
//
// Writes must run under service_role — there is deliberately no authenticated
// INSERT policy on selection_audit_events, so the log is append-only and
// tamper-resistant. Reads are account-scoped for every voter (rendered on the
// results page; an empty log is itself the trust signal).
// ============================================

export async function logSelectionAudit(input: SelectionAuditEventCreate): Promise<SelectionAuditEvent> {
  const { data, error } = await getClient()
    .from(TABLES.selection_audit_events)
    .insert({
      account_id: input.account_id,
      selection_id: input.selection_id,
      round_id: input.round_id ?? null,
      actor_user_id: input.actor_user_id ?? null,
      action: input.action,
      scope: input.scope ?? null,
      metadata: input.metadata ?? {},
    })
    .select()
    .single();

  if (error) throw error;
  return data as SelectionAuditEvent;
}

export async function listSelectionAudit(selectionId: string): Promise<SelectionAuditEvent[]> {
  const { data, error } = await getClient()
    .from(TABLES.selection_audit_events)
    .select('*')
    .eq('selection_id', selectionId)
    .order('created_at', { ascending: false });

  if (error) throw error;
  return (data || []) as SelectionAuditEvent[];
}

// ============================================
// Reveal: nomination tally → shortlist
//
// These read across ALL voters' ballots, which RLS hides pre-reveal — so the
// caller (API) must run them under service_role. The reveal transition is
// irreversible and is audited.
// ============================================

/**
 * Count nominations per happening across submitted ballots in a nominate round,
 * and note which happenings received at least one wildcard.
 */
export async function tallyNominations(roundId: string): Promise<{
  counts: Record<string, number>;
  wildcardHappeningIds: string[];
  ballotCount: number;
}> {
  const ballots = await listRoundBallots(roundId);
  const submitted = ballots.filter((b) => b.status === 'submitted');
  const counts: Record<string, number> = {};
  const wildcards = new Set<string>();
  for (const b of submitted) {
    for (const e of b.entries) {
      counts[e.happening_id] = (counts[e.happening_id] ?? 0) + 1;
      if (e.is_wildcard) wildcards.add(e.happening_id);
    }
  }
  return { counts, wildcardHappeningIds: Array.from(wildcards), ballotCount: submitted.length };
}

function buildShortlist(
  counts: Record<string, number>,
  wildcards: Set<string>,
  rule: CutoffRule,
  include: string[],
  exclude: string[],
): string[] {
  const set = new Set<string>();
  if (rule.type === 'threshold') {
    for (const [h, c] of Object.entries(counts)) if (c >= rule.min) set.add(h);
  } else {
    // Top-K by nomination count. Tie-inclusive: never split a tie at the
    // boundary — include everyone whose count is >= the K-th show's count, so a
    // show is never arbitrarily excluded ahead of an equally-nominated one.
    // (This can yield more than K shows; the reveal preview shows the real size.)
    const sorted = Object.entries(counts).sort((a, b) => b[1] - a[1]);
    const k = Math.max(0, rule.k);
    if (k >= sorted.length) {
      for (const [h] of sorted) set.add(h);
    } else if (k > 0) {
      const cutoffCount = sorted[k - 1][1];
      for (const [h, c] of sorted) if (c >= cutoffCount) set.add(h);
    }
  }
  for (const w of wildcards) set.add(w); // wildcards are unioned in
  for (const i of include) set.add(i); // manual include
  for (const e of exclude) set.delete(e); // manual exclude (owner override, logged)
  return Array.from(set);
}

/**
 * Identity-free cutoff preview: the nomination-count distribution and the
 * resulting shortlist SIZE under each candidate rule. Returns no happening
 * identities, so it's safe to show the owner while the round is still closed
 * ("projected shortlist size as a number only").
 */
export async function cutoffPreview(
  roundId: string,
  targetShortlist?: number,
): Promise<{
  ballotCount: number;
  nominatedCount: number;
  maxCount: number;
  distribution: Record<number, number>;
  wildcardCount: number;
  /** min nominations → resulting shortlist size (∪ wildcards), for min=1..maxCount. */
  thresholdSizes: Record<number, number>;
  options: Array<{ rule: CutoffRule; size: number }>;
}> {
  const { counts, wildcardHappeningIds, ballotCount } = await tallyNominations(roundId);
  const wildcards = new Set(wildcardHappeningIds);
  const values = Object.values(counts);
  const maxCount = values.length > 0 ? Math.max(...values) : 0;

  const distribution: Record<number, number> = {};
  for (const c of values) distribution[c] = (distribution[c] ?? 0) + 1;

  const size = (rule: CutoffRule) => buildShortlist(counts, wildcards, rule, [], []).length;

  const thresholdSizes: Record<number, number> = {};
  for (let m = 1; m <= maxCount; m++) thresholdSizes[m] = size({ type: 'threshold', min: m });

  const options: Array<{ rule: CutoffRule; size: number }> = [
    { rule: { type: 'threshold', min: 2 }, size: thresholdSizes[2] ?? wildcards.size },
    { rule: { type: 'threshold', min: 3 }, size: thresholdSizes[3] ?? wildcards.size },
  ];
  if (targetShortlist && targetShortlist > 0) {
    options.push({ rule: { type: 'top', k: targetShortlist }, size: size({ type: 'top', k: targetShortlist }) });
  }

  return {
    ballotCount,
    nominatedCount: values.length,
    maxCount,
    distribution,
    wildcardCount: wildcards.size,
    thresholdSizes,
    options,
  };
}

export interface RevealInput {
  selectionId: string;
  roundId: string;
  accountId: string;
  rule: CutoffRule;
  include?: string[];
  exclude?: string[];
  reason?: string | null;
  actorUserId?: string | null;
}

/**
 * Compute the shortlist under the chosen cutoff (∪ wildcards, ± manual
 * include/exclude) and store it on the round's config. Refuses to produce an
 * empty shortlist. Shared by reveal (first disclosure) and re-cut.
 */
async function computeAndStoreShortlist(
  roundId: string,
  rule: CutoffRule,
  include: string[],
  exclude: string[],
): Promise<{ shortlist: string[]; round: SelectionRound }> {
  const round = await getRound(roundId);
  if (!round) throw new SelectionValidationError('Round not found');
  if (round.kind !== 'nominate') {
    throw new SelectionValidationError('Only nominate rounds produce a shortlist');
  }

  const { counts, wildcardHappeningIds } = await tallyNominations(roundId);
  const shortlist = buildShortlist(counts, new Set(wildcardHappeningIds), rule, include, exclude);
  if (shortlist.length === 0) {
    throw new SelectionValidationError(
      'This cutoff produces an empty shortlist — lower the threshold or use Top-K.',
    );
  }

  const updated = await updateRound(roundId, {
    config: {
      ...round.config,
      shortlist_happening_ids: shortlist,
      cutoff_rule: rule,
      manual_include: include,
      manual_exclude: exclude,
    } as Record<string, unknown>,
  });
  return { shortlist, round: updated };
}

/**
 * First disclosure: generate the shortlist, flip the round closed → revealed,
 * and audit. From here ballots become visible to the whole account (RLS).
 */
export async function revealNominateRound(
  input: RevealInput,
): Promise<{ shortlist: string[]; round: SelectionRound }> {
  const round = await getRound(input.roundId);
  if (!round) throw new SelectionValidationError('Round not found');
  if (round.state !== 'closed') {
    throw new SelectionValidationError(`Round must be closed to reveal (is ${round.state})`);
  }

  const { shortlist } = await computeAndStoreShortlist(
    input.roundId,
    input.rule,
    input.include ?? [],
    input.exclude ?? [],
  );
  const revealed = await transitionRound(input.roundId, 'revealed');

  await logSelectionAudit({
    account_id: input.accountId,
    selection_id: input.selectionId,
    round_id: input.roundId,
    actor_user_id: input.actorUserId ?? null,
    action: 'reveal_round',
    scope: `revealed round ${round.ordinal}; shortlist of ${shortlist.length}`,
    metadata: {
      rule: input.rule,
      include: input.include ?? [],
      exclude: input.exclude ?? [],
      reason: input.reason ?? null,
    },
  });

  return { shortlist, round: revealed };
}

/**
 * Re-cut an already-revealed round: regenerate the shortlist from the same
 * (already-visible) ballots under a different cutoff/method. The round stays
 * 'revealed' — nothing is re-hidden — and each re-cut is audited. Note: an
 * allocate round snapshots the shortlist when created, so a re-cut does not
 * retro-change a round-2 already in progress.
 */
export async function recutShortlist(
  input: RevealInput,
): Promise<{ shortlist: string[]; round: SelectionRound }> {
  const round = await getRound(input.roundId);
  if (!round) throw new SelectionValidationError('Round not found');
  if (round.state !== 'revealed') {
    throw new SelectionValidationError(`Round must be revealed to re-cut (is ${round.state})`);
  }

  const { shortlist, round: updated } = await computeAndStoreShortlist(
    input.roundId,
    input.rule,
    input.include ?? [],
    input.exclude ?? [],
  );

  await logSelectionAudit({
    account_id: input.accountId,
    selection_id: input.selectionId,
    round_id: input.roundId,
    actor_user_id: input.actorUserId ?? null,
    action: 'recut_shortlist',
    scope: `re-cut round ${round.ordinal}; shortlist of ${shortlist.length}`,
    metadata: {
      rule: input.rule,
      include: input.include ?? [],
      exclude: input.exclude ?? [],
      reason: input.reason ?? null,
    },
  });

  return { shortlist, round: updated };
}

/**
 * Reveal an allocate round: flip closed → revealed (disclosing all ballots via
 * RLS) and audit. No cutoff — the ranking comes from the tally. Irreversible.
 */
export async function revealAllocateRound(input: {
  selectionId: string;
  roundId: string;
  accountId: string;
  actorUserId?: string | null;
}): Promise<{ round: SelectionRound }> {
  const round = await getRound(input.roundId);
  if (!round) throw new SelectionValidationError('Round not found');
  if (round.kind !== 'allocate') {
    throw new SelectionValidationError('Only allocate rounds are revealed this way');
  }
  if (round.state !== 'closed') {
    throw new SelectionValidationError(`Round must be closed to reveal (is ${round.state})`);
  }

  const revealed = await transitionRound(input.roundId, 'revealed');
  await logSelectionAudit({
    account_id: input.accountId,
    selection_id: input.selectionId,
    round_id: input.roundId,
    actor_user_id: input.actorUserId ?? null,
    action: 'reveal_round',
    scope: `revealed allocate round ${round.ordinal}`,
    metadata: {},
  });
  return { round: revealed };
}

/**
 * Tally an allocate round into ranked rows. Each voter's points are normalized
 * to shares (points / that voter's total) so a recused voter spreading the same
 * budget over fewer items isn't over-weighted; `score` sums those shares.
 * Ranked by score, then raw points. Reads all ballots — only meaningful once
 * the round is revealed (RLS returns only the caller's own ballot before that).
 */
export async function tallyAllocateRound(
  roundId: string,
): Promise<{ rows: TallyRow[]; ballotCount: number }> {
  const ballots = await listRoundBallots(roundId);
  const submitted = ballots.filter((b) => b.status === 'submitted');

  const raw: Record<string, number> = {};
  const score: Record<string, number> = {};
  const voters: Record<string, Set<string>> = {};

  for (const b of submitted) {
    const total = b.entries.reduce((s, e) => s + (e.points ?? 0), 0);
    for (const e of b.entries) {
      const pts = e.points ?? 0;
      raw[e.happening_id] = (raw[e.happening_id] ?? 0) + pts;
      if (total > 0) score[e.happening_id] = (score[e.happening_id] ?? 0) + pts / total;
      (voters[e.happening_id] ??= new Set()).add(b.voter_id);
    }
  }

  const rows: TallyRow[] = Object.keys(raw).map((hid) => ({
    happening_id: hid,
    raw_points: raw[hid],
    score: score[hid] ?? 0,
    voter_count: voters[hid]?.size ?? 0,
  }));
  rows.sort((a, b) => b.score - a.score || b.raw_points - a.raw_points);

  return { rows, ballotCount: submitted.length };
}
